import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import orbitIcon from "@/assets/orbit-icon.png";
import AnimatedLogo from "@/components/AnimatedLogo";

export default function Login() {
  const [email, setEmail] = useState("admin@orbit.io");
  const [password, setPassword] = useState("admin123");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { onAuth } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onAuth(data);
      const last = localStorage.getItem("last-project-id");
      nav(loc.state?.from || "/home");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-white border-r border-border">
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-foreground">
          <div className="flex items-center gap-2">
            <img src={orbitIcon} alt="Orbit" className="h-9 w-auto" />
            <span className="font-display text-xl font-extrabold tracking-tight">Project Management Orchestrator</span>
          </div>
          <div>
            <div className="max-w-sm mb-4">
              <AnimatedLogo />
            </div>
            <p className="font-display text-lg font-bold tracking-tight text-primary mb-8" data-testid="brand-tagline">Plan. Manage. Orchestrate.<br /><span className="text-base">PMO</span></p>
            <p className="overline text-muted-foreground mb-3">Industry best-practice aligned</p>
            <h1 className="font-display text-5xl font-extrabold tracking-tight leading-[1.05]">
              Turn scope into a structured plan, timeline &amp; next steps.
            </h1>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src={orbitIcon} alt="Orbit" className="h-8 w-auto" />
            <span className="font-display text-xl font-extrabold">Project Management Orchestrator</span>
          </div>
          <p className="overline text-muted-foreground mb-2">Welcome back</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mb-8">Sign in to your workspace</h2>

          {error && <div className="mb-4 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2" data-testid="login-error">{error}</div>}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1.5" />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full mt-6 h-11" data-testid="login-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
          </Button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            No account?{" "}
            <Link to="/register" className="text-primary font-semibold hover:underline" data-testid="go-register-link">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
